/*
 * WorkflowToolbar — top bar for the workflow designer.
 * Shows back button, editable workflow name, save, publish/unpublish, run button.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RunWorkflowDialog from './RunWorkflowDialog';
import { useCanBuildWorkflows } from '../../identity/useIsAdmin';

const styles = {
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 16px',
    borderBottom: '1px solid #e5e7eb',
    background: 'white',
    flexShrink: 0,
    minHeight: 48,
    position: 'relative',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    color: '#6b7280',
    padding: '4px 8px',
    borderRadius: 4,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  divider: {
    width: 1,
    height: 24,
    background: '#e5e7eb',
    flexShrink: 0,
  },
  nameDisplay: {
    fontSize: 15,
    fontWeight: 600,
    color: '#111827',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: 4,
    border: '1px solid transparent',
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nameInput: {
    fontSize: 15,
    fontWeight: 600,
    color: '#111827',
    padding: '2px 6px',
    borderRadius: 4,
    border: '1px solid var(--stm-accent-info)',
    outline: 'none',
    width: 260,
    boxShadow: '0 0 0 3px rgba(59,130,246,0.1)',
  },
  spacer: { flex: 1 },
  badge: (published) => ({
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    background: published ? '#d1fae5' : '#f3f4f6',
    color: published ? '#065f46' : '#6b7280',
  }),
  btn: {
    padding: '6px 14px',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    background: 'white',
    fontSize: 13,
    fontWeight: 500,
    color: '#374151',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  btnPrimary: {
    padding: '6px 14px',
    borderRadius: 6,
    border: 'none',
    background: '#7c3aed',
    fontSize: 13,
    fontWeight: 500,
    color: 'white',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  btnRun: {
    padding: '6px 14px',
    borderRadius: 6,
    border: 'none',
    background: '#10b981',
    fontSize: 13,
    fontWeight: 500,
    color: 'white',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  saveIndicator: {
    fontSize: 11,
    color: '#9ca3af',
    whiteSpace: 'nowrap',
  },
};

const WorkflowToolbar = ({
  workflow,
  saving,
  dirty,
  running,
  batchProgress,
  onSave,
  onPublish,
  onUnpublish,
  onNameChange,
  onRun,
  onOpenSettings,
}) => {
  const navigate = useNavigate();
  // R-publish REVERSED (2026-06-14): the full workflow lifecycle — publish /
  // unpublish + the ⚙ settings panel — is for builders (admin||power_user).
  // No workflow control is admin-only anymore.
  const canBuild = useCanBuildWorkflows();
  const [editing, setEditing] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const inputRef = useRef(null);
  const [showRunDialog, setShowRunDialog] = useState(false);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEditing = () => {
    setNameValue(workflow?.name || '');
    setEditing(true);
  };

  const finishEditing = () => {
    setEditing(false);
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== workflow?.name) {
      onNameChange(trimmed);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') finishEditing();
    if (e.key === 'Escape') setEditing(false);
  };

  const handleRunClick = () => {
    if (running) return;
    setShowRunDialog(true);
  };

  const handleExecute = (documentIds) => {
    setShowRunDialog(false);
    onRun(documentIds);
  };

  return (
    <div style={styles.toolbar}>
      <button
        style={styles.backBtn}
        onClick={() => navigate('/workflows')}
        title="Back to Workflows"
      >
        ← Workflows
      </button>

      <div style={styles.divider} />

      {editing ? (
        <input
          ref={inputRef}
          style={styles.nameInput}
          value={nameValue}
          onChange={(e) => setNameValue(e.target.value)}
          onBlur={finishEditing}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <span
          style={styles.nameDisplay}
          onClick={startEditing}
          title="Click to rename"
        >
          {workflow?.name || 'Untitled Workflow'} ✏️
        </span>
      )}

      <span style={styles.badge(workflow?.is_published)}>
        {workflow?.is_published ? 'Published' : 'Draft'}
      </span>

      <div style={styles.spacer} />

      {dirty && <span style={styles.saveIndicator}>Unsaved changes</span>}

      <button
        style={{ ...styles.btnPrimary, ...(saving ? styles.btnDisabled : {}) }}
        onClick={onSave}
        disabled={saving}
        title="Save workflow"
      >
        {saving ? 'Saving...' : '💾 Save'}
      </button>

      {/* R-publish REVERSED (2026-06-14): publish & unpublish are builder
          actions (admin||power_user), gated by canBuildWorkflows — server
          enforces via accessPolicy buildOnly. (Whole designer route is
          builder-guarded.) The split-strategy selector is NOT touched here
          (BS1 CP3, kept separate). */}
      {canBuild && (workflow?.is_published ? (
        <button style={styles.btn} onClick={onUnpublish}>
          Unpublish
        </button>
      ) : (
        <button style={styles.btn} onClick={onPublish}>
          Publish
        </button>
      ))}

      {/* Cluster 5 Slice 2 / A8 pt 5: workflow-settings panel (per-workflow
          Pathway D toggle). R-panel (2026-06-14): builder-gated (admin||power_user)
          — a power_user configures the workflow they build. */}
      {canBuild && onOpenSettings && (
        <button style={styles.btn} onClick={onOpenSettings} title="Workflow settings">
          ⚙ Settings
        </button>
      )}

      <button
        style={{ ...styles.btnRun, ...(running ? styles.btnDisabled : {}) }}
        onClick={handleRunClick}
        disabled={running}
        title={running ? 'Workflow is running...' : 'Run workflow'}
      >
        {running && batchProgress
          ? `⏳ Running ${batchProgress.completed + batchProgress.failed}/${batchProgress.total}...`
          : running
            ? '⏳ Running...'
            : '▶ Run'}
      </button>

      {running && batchProgress && batchProgress.total > 1 && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
          background: '#e5e7eb',
        }}>
          <div style={{
            height: '100%',
            width: `${Math.round(((batchProgress.completed + batchProgress.failed) / batchProgress.total) * 100)}%`,
            background: batchProgress.failed > 0 ? '#f59e0b' : '#10b981',
            transition: 'width 0.3s ease',
          }} />
        </div>
      )}

      <RunWorkflowDialog
        open={showRunDialog}
        onClose={() => setShowRunDialog(false)}
        onExecute={handleExecute}
      />
    </div>
  );
};

export default WorkflowToolbar;
